import { useState, useCallback } from 'react';

/**
 * COMPREHENSIVE FILE TYPE CONFIGURATION
 * WHY: Centralized configuration makes it easy to add/remove file types
 * 
 * Supports:
 * - Images: JPEG, PNG, TIFF, DICOM (.dcm)
 * - Documents: PDF, DOCX, TXT
 * - Spreadsheets: CSV, XLSX
 * - Structured data: JSON, XML
 */
const FILE_TYPES = {
  // Image file types - commonly used formats
  IMAGES: {
    category: 'image',
    label: 'Images',
    extensions: ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.dcm'],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/tiff',
      'application/dicom', // DICOM files
      'image/x-dcm',
    ],
    maxSize: 10 * 1024 * 1024, // 10MB for images
  },
  // Document file types - office and text formats
  DOCUMENTS: {
    category: 'document',
    label: 'Documents',
    extensions: ['.pdf', '.docx', '.txt'],
    mimeTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'text/plain',
    ],
    maxSize: 15 * 1024 * 1024, // 15MB for documents
  },
  // Spreadsheet file types
  SPREADSHEETS: {
    category: 'spreadsheet',
    label: 'Spreadsheets',
    extensions: ['.csv', '.xlsx'],
    mimeTypes: [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
      'application/x-xlsx',
    ],
    maxSize: 20 * 1024 * 1024, // 20MB for spreadsheets
  },
  // Structured data file types
  STRUCTURED_DATA: {
    category: 'structured',
    label: 'Structured Data',
    extensions: ['.json', '.xml'],
    mimeTypes: [
      'application/json',
      'text/xml',
      'application/xml',
    ],
    maxSize: 25 * 1024 * 1024, // 25MB for structured data
  },
};

/**
 * Global validation configuration
 * WHY: Prevents configuration changes during execution
 */
const FILE_VALIDATION = {
  // Maximum total files per message
  MAX_FILES: 10,
  // Maximum total upload size per message (100MB)
  MAX_TOTAL_SIZE: 100 * 1024 * 1024,
};

/**
 * Custom hook for managing multi-type file uploads with validation
 * 
 * CHANGES FROM PREVIOUS VERSION:
 * - Renamed from useImageUpload to useFileUpload for clarity
 * - Supports multiple file categories (images, documents, spreadsheets, data)
 * - Dynamic file type/extension validation
 * - Category-specific size limits
 * - Total upload size tracking
 * - Detailed file metadata (type, category, size, extension)
 * - Progress tracking by file
 * - Better error messages with file type info
 * 
 * @param {Array} allowedCategories - Categories to allow (e.g., ['image', 'document'])
 * @returns {Object} - File management functions, state, and validation info
 */
export const useFileUpload = (allowedCategories = ['image']) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalUploadSize, setTotalUploadSize] = useState(0);

  /**
   * Build list of all allowed MIME types based on categories
   * WHY: Enables filtering file input based on what user wants to upload
   */
  const getAllowedMimeTypes = useCallback(() => {
    return allowedCategories
      .map(category => {
        const typeConfig = Object.values(FILE_TYPES).find(t => t.category === category);
        return typeConfig?.mimeTypes || [];
      })
      .flat();
  }, [allowedCategories]);

  /**
   * Build list of all allowed extensions based on categories
   * WHY: Enables showing helpful accept strings to users
   */
  const getAllowedExtensions = useCallback(() => {
    return allowedCategories
      .map(category => {
        const typeConfig = Object.values(FILE_TYPES).find(t => t.category === category);
        return typeConfig?.extensions || [];
      })
      .flat();
  }, [allowedCategories]);

  /**
   * Get file type configuration by MIME type or extension
   * WHY: Enables category-specific size limits and error messages
   */
  const getFileTypeConfig = useCallback((file) => {
    // Try matching by MIME type first (more reliable)
    for (const typeConfig of Object.values(FILE_TYPES)) {
      if (typeConfig.mimeTypes.includes(file.type)) {
        return typeConfig;
      }
    }

    // Fallback: try matching by file extension
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    for (const typeConfig of Object.values(FILE_TYPES)) {
      if (typeConfig.extensions.includes(extension)) {
        return typeConfig;
      }
    }

    return null;
  }, []);

  /**
   * Validate individual file for type, size, extension, and category
   * WHY: Prevents invalid files from being processed and provides detailed feedback
   * 
   * @param {File} file - File to validate
   * @returns {Object} - { isValid: boolean, error: string|null, category: string|null }
   */
  const validateFile = useCallback((file) => {
    // Get file extension
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    // Get file type configuration
    const typeConfig = getFileTypeConfig(file);

    // Check if file type is supported
    if (!typeConfig) {
      const allowedExt = getAllowedExtensions().join(', ');
      return {
        isValid: false,
        error: `File type not supported: "${file.name}". Allowed formats: ${allowedExt || 'None selected'}`,
        category: null,
      };
    }

    // Check if category is in allowed list
    if (!allowedCategories.includes(typeConfig.category)) {
      return {
        isValid: false,
        error: `${typeConfig.label} files are not allowed in this context. Only: ${allowedCategories.map(cat => Object.values(FILE_TYPES).find(t => t.category === cat)?.label).filter(Boolean).join(', ')}`,
        category: null,
      };
    }

    // Check file extension validity
    if (!typeConfig.extensions.includes(extension)) {
      const validExt = typeConfig.extensions.join(', ');
      return {
        isValid: false,
        error: `Invalid extension for ${typeConfig.label}: "${extension}". Valid: ${validExt}`,
        category: typeConfig.category,
      };
    }

    // Check file size against category-specific limit
    if (file.size > typeConfig.maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxMB = (typeConfig.maxSize / (1024 * 1024)).toFixed(0);
      return {
        isValid: false,
        error: `${typeConfig.label} file too large: ${sizeMB}MB (max ${maxMB}MB)`,
        category: typeConfig.category,
      };
    }

    return { isValid: true, error: null, category: typeConfig.category };
  }, [getFileTypeConfig, getAllowedExtensions, allowedCategories]);

  /**
   * Handle file selection with comprehensive validation
   * WHY: Prevents adding invalid files and validates each file before processing
   */
  const handleFileSelect = useCallback((e) => {
    // Clear previous errors when new files are selected
    setError(null);
    setUploadProgress(0);

    try {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      // Check if adding these files would exceed the limit
      const totalFiles = selectedFiles.length + files.length;
      if (totalFiles > FILE_VALIDATION.MAX_FILES) {
        setError(
          `Cannot add ${files.length} file(s). Maximum ${FILE_VALIDATION.MAX_FILES} files allowed. Currently have ${selectedFiles.length}.`
        );
        return;
      }

      const validFiles = [];
      const errors = [];
      let tempTotalSize = totalUploadSize;

      // Validate each file
      files.forEach((file) => {
        const validation = validateFile(file);
        if (validation.isValid) {
          // Check total size constraint
          if (tempTotalSize + file.size > FILE_VALIDATION.MAX_TOTAL_SIZE) {
            const totalMB = ((tempTotalSize + file.size) / (1024 * 1024)).toFixed(0);
            const maxMB = (FILE_VALIDATION.MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(0);
            errors.push(`Total upload size (${totalMB}MB) exceeds limit (${maxMB}MB)`);
            return;
          }

          try {
            // Create preview URL for files that support it (images)
            let preview = null;
            if (validation.category === 'image') {
              preview = URL.createObjectURL(file);
            }

            const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
            const typeConfig = getFileTypeConfig(file);

            validFiles.push({
              file,
              preview,
              name: file.name,
              size: file.size,
              type: file.type,
              category: validation.category,
              extension,
              typeLabel: typeConfig?.label || 'File',
            });

            tempTotalSize += file.size;
          } catch (err) {
            // WHY: Catch object URL creation failures
            errors.push(`Failed to process ${file.name}`);
          }
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      // Set error if any files failed validation
      if (errors.length > 0) {
        setError(errors.join('\n'));
      }

      // Add valid files to state
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
        setTotalUploadSize(tempTotalSize);
        setUploadProgress(100);
        // Reset progress after brief delay
        setTimeout(() => setUploadProgress(0), 1000);
      }
    } catch (err) {
      // WHY: Catch any unexpected errors to prevent crash
      console.error('Error handling file selection:', err);
      setError('An unexpected error occurred while processing files. Please try again.');
    }
  }, [selectedFiles.length, validateFile, totalUploadSize, getFileTypeConfig]);

  /**
   * Remove file by index with proper cleanup
   * WHY: Revokes object URLs to free memory and prevent memory leaks
   */
  const removeFile = useCallback((index) => {
    try {
      setSelectedFiles((prev) => {
        // WHY: Bounds check prevents crash from invalid array index
        if (index < 0 || index >= prev.length) {
          console.warn(`Invalid file index: ${index}`);
          return prev;
        }
        const updated = [...prev];
        const removedFile = updated[index];

        // Update total size
        setTotalUploadSize((prevSize) => Math.max(0, prevSize - (removedFile?.size || 0)));

        // WHY: Check for preview existence before revoking (defensive programming)
        if (removedFile?.preview) {
          URL.revokeObjectURL(removedFile.preview);
        }
        updated.splice(index, 1);
        return updated;
      });
      // Clear error when user removes a file
      setError(null);
    } catch (err) {
      // WHY: Catch errors to prevent crash during cleanup
      console.error('Error removing file:', err);
    }
  }, []);

  /**
   * Clear all files with proper memory cleanup
   * WHY: Ensures all object URLs are revoked to prevent memory leaks
   */
  const clearFiles = useCallback(() => {
    try {
      selectedFiles.forEach((fileItem) => {
        // WHY: Check for preview existence before revoking
        if (fileItem?.preview) {
          URL.revokeObjectURL(fileItem.preview);
        }
      });
      setSelectedFiles([]);
      setTotalUploadSize(0);
      setError(null);
      setUploadProgress(0);
    } catch (err) {
      // WHY: Catch errors to prevent crash during cleanup
      console.error('Error clearing files:', err);
    }
  }, [selectedFiles]);

  /**
   * Reset files (typically used after sending message)
   */
  const resetFiles = useCallback(() => {
    clearFiles();
  }, [clearFiles]);

  /**
   * Clear error message for dismissible error UI
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get total upload size in human-readable format
   */
  const getTotalSizeFormatted = useCallback(() => {
    const mb = (totalUploadSize / (1024 * 1024)).toFixed(2);
    return `${mb}MB`;
  }, [totalUploadSize]);

  return {
    // File state
    selectedFiles,
    totalUploadSize,
    getTotalSizeFormatted,
    // File management
    handleFileSelect,
    removeFile,
    clearFiles,
    resetFiles,
    // Validation & config
    validateFile,
    getFileTypeConfig,
    getAllowedMimeTypes,
    getAllowedExtensions,
    // Error handling
    error,
    clearError,
    // Progress
    uploadProgress,
    // Limits
    maxFiles: FILE_VALIDATION.MAX_FILES,
    maxTotalSize: FILE_VALIDATION.MAX_TOTAL_SIZE,
  };
};

// Export individual file type configs for reference
export { FILE_TYPES, FILE_VALIDATION };
